import { durationStringToSeconds, formatDurationForDisplay, formattedDate } from '@talos/kyoko';
import Big from 'big.js';
import type { PreTradeResponseData } from '../../../redux/preTradeApi';
import { calcRatioAsPercent, deriveDurationFromTimestamps } from '../../../utils';
import type { PreTradeAnalyticName, PreTradePredictionRow } from './usePreTradePredictionColumns';
import { getNumeric, toPercentNumber } from './utils';

function toNumericRow(
  name: PreTradeAnalyticName,
  interval: number | undefined,
  daily: number | undefined
): PreTradePredictionRow {
  return {
    name,
    interval: interval != null ? Big(interval) : null,
    daily: daily != null ? Big(daily) : null,
    vs: calcRatioAsPercent(interval, daily),
  };
}

/**
 * Normalizes raw scenario analytics/timestamps into values consumed by the
 * Trade Predictions table. Duration prefers `Parameters.Duration` and falls
 * back to deriving from response `StartTime`/`EndTime` when missing.
 */
export function transformPreTradeValues(scenario?: PreTradeResponseData) {
  const analytics = scenario?.Analytics;
  const duration =
    scenario?.NewOrderSingle?.Parameters?.Duration ||
    deriveDurationFromTimestamps(scenario?.StartTime, scenario?.EndTime);

  return {
    startTime: scenario?.StartTime ? formattedDate(scenario.StartTime) : null,
    endTime: scenario?.EndTime ? formattedDate(scenario.EndTime) : null,
    intervalSpread: getNumeric(analytics, 'IntervalSpreadBps'),
    dailySpread: getNumeric(analytics, 'DailySpreadBps'),
    intervalVolume: getNumeric(analytics, 'IntervalVolume'),
    dailyVolume: getNumeric(analytics, 'AverageDailyVolume'),
    intervalVolatility: getNumeric(analytics, 'IntervalVolatilityBps'),
    dailyVolatility: getNumeric(analytics, 'DailyVolatilityBps'),
    participationInterval: toPercentNumber(getNumeric(analytics, 'PredictedParticipationRatePct')),
    participationDaily: toPercentNumber(getNumeric(analytics, 'OrderAverageDailyVolumePct')),
    duration,
  };
}

export type TransformedPreTradeValues = ReturnType<typeof transformPreTradeValues>;

/** Maps pre-trade scenario data to rows for the Trade Predictions table (interval vs daily vs columns). */
export function getPreTradePredictionRows(scenario?: PreTradeResponseData): PreTradePredictionRow[] {
  const values = transformPreTradeValues(scenario);
  const durationIntervalSeconds = durationStringToSeconds(values.duration);
  const durationDailySeconds = durationStringToSeconds('24h');

  return [
    {
      name: 'Start Time',
      interval: values.startTime,
      daily: null,
      vs: null,
    },
    {
      name: 'End Time',
      interval: values.endTime,
      daily: null,
      vs: null,
    },
    toNumericRow('Spread', values.intervalSpread, values.dailySpread),
    toNumericRow('Volume', values.intervalVolume, values.dailyVolume),
    toNumericRow('Participation Rate', values.participationInterval, values.participationDaily),
    toNumericRow('Volatility', values.intervalVolatility, values.dailyVolatility),
    {
      name: 'Duration',
      interval: formatDurationForDisplay(values.duration) ?? values.duration ?? '-',
      daily: '24h',
      vs: calcRatioAsPercent(durationIntervalSeconds, durationDailySeconds),
    },
  ];
}
